// ─────────────────────────────────────────────────────────────────────────────
// PulseSchedulePanel — the PULSE SCHEDULE front end (Stage-2 Step 2).
//
// PROOF OF LIFE. She pulses hourly, on the hour. This panel is where the owner
// watches that heartbeat: green ✓ for a pulse that landed, red ✗ for one that
// did not. "She pulsed at 3:00pm ✓, 4:00pm ✓, and missed 2:00am ✗."
//
// Time is the through-thread — the display must be EXACT, correctly ordered
// (newest-first, exactly what the backend accrued), and it NEVER hides a
// failure. No fabricated history: a fresh ledger renders honestly empty, an
// unreadable ledger renders a visible critical line rather than silence.
//
// ONE CLOCK. Every label here is rendered in `schedule.timezone` — the stored
// IANA world clock (core/rexy_clock.py, written by the settings page), which the
// payload carries. NEVER the browser's zone: a 16:00+10:00 pulse renders 06:00 in
// a UTC browser and 02:00 in New York, so a device-zone panel silently contradicts
// the real timeline it exists to prove.
//
// Data source: GET /api/rexy/pulse-schedule (Bearer (window as any).__rexyToken),
// fetched on mount + polled every 30s — same auth/poll/error/loading discipline
// as ActivityPanel (Step 1, JOINT-signed). The response also carries `reports`;
// this panel IGNORES it.
//
// Palette: REXY_CONSOLE only, plus the one local pulse-green (see PULSE_OK).
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';
import { usePulseSchedule } from '../../data/dashboard';
import type { PulseSchedule } from '../../data/dashboard';

const MONO = REXY_FONT;

// console-family neon green — the pulse-green; no green exists in REXY_CONSOLE
const PULSE_OK = '#00FF9C';

// ── contract (pinned; the backend builds to exactly this) ────────────────────
//
// DESKTOP ARRANGEMENT ONLY. The contract types, the endpoint/poll and the ONE
// clock moved to data/dashboard/ under the 2026-07-31 "one core, every window"
// ruling — the portrait phone surface renders the SAME feed in a different
// arrangement. Re-exported here so every existing importer (NotesPanel,
// ReportsPanel, DreamstatePanel and PulseScheduleWorldClock.test.tsx) keeps
// resolving to the identical implementation. The render below is unchanged.
export type { PulseRow, PulseSchedule, PulseScheduleResponse } from '../../data/dashboard';

// The ONE clock — exported so the world-clock regression can exercise it
// directly as well as through a render.
export { makeTimeFormatter } from '../../data/dashboard';

export default function PulseSchedulePanel() {
  const { schedule, err, hourLabel } = usePulseSchedule();

  function renderBody(s: PulseSchedule) {
    const footerOn = s.pending_retries > 0 || s.abandoned > 0 || s.malformed > 0;
    return (
      <>
        {/* NEXT FIRES — the future, first. Rendered in every ledger state. */}
        {(s.next_pulses || []).map((iso, i) => (
          <div key={`next${i}`} style={styles.nextRow}>
            <span style={styles.nextGlyph}>{'·'}</span>
            <span style={styles.nextLabel}>next {hourLabel(iso)}</span>
          </div>
        ))}

        {/* Unreadable ledger — a visible failure, never silence. */}
        {s.ledger_status === 'unreadable' ? (
          <div style={styles.critical}>
            pulse ledger unreadable {'—'} tick refused (visible failure)
          </div>
        ) : s.ledger_status === 'fresh' || (s.pulses || []).length === 0 ? (
          // Honest empty history — no fabricated rows, ever.
          <div style={styles.emptyHistory}>no pulses recorded yet {'—'} next fires above</div>
        ) : (
          <div style={styles.history}>
            {s.pulses.map((p) => {
              const ok = p.result === 'green';
              const color = ok ? PULSE_OK : REXY_CONSOLE.critical;
              return (
                <div key={p.slot_id} style={styles.itemRow} title={p.slot_id}>
                  <span style={styles.rowHour}>{hourLabel(p.scheduled_for)}</span>
                  <span style={{ ...styles.rowGlyph, color }}>{ok ? '✓' : '✗'}</span>
                  <span style={styles.rowStatus}>{p.status}</span>
                  {p.events_written > 0 ? (
                    <span style={styles.rowEvents}>{p.events_written} events</span>
                  ) : null}
                  {p.failures > 0 ? (
                    <span style={styles.rowFailures}>{p.failures} fail</span>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {footerOn ? (
          <div style={styles.footer}>
            retries {s.pending_retries} {'·'} abandoned {s.abandoned} {'·'} malformed {s.malformed}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div style={styles.card}>
      {/* Meta strap REMOVED (owner ruling 2026-07-24): the panel keeps its title and its
          content (the accrued slots + next fires); the "her pulse · hourly · <zone> ·
          updated…" line is gone. */}
      <div style={styles.headerRow}>
        <div style={styles.sectionHeader}>{'║'} PULSE SCHEDULE</div>
      </div>

      {err ? (
        <div style={styles.degraded}>not_available {'—'} {err}</div>
      ) : !schedule ? (
        <div style={styles.degraded}>loading{'…'}</div>
      ) : (
        renderBody(schedule)
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 14,
    // Dimensional, never flat (CLAUDE.md UI §3) — same bubble idiom as ActivityPanel.
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 3,
    marginBottom: 10,
    minWidth: 0,
  },
  sectionHeader: {
    fontWeight: 700,
    fontFamily: MONO,
    fontSize: 12,
    letterSpacing: 2,
    color: REXY_CONSOLE.primary,
  },
  nextRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    padding: '2px 4px',
  },
  nextGlyph: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.dimText },
  nextLabel: {
    fontFamily: MONO,
    fontSize: 10,
    letterSpacing: 1,
    color: REXY_CONSOLE.dimText,
    whiteSpace: 'nowrap',
  },
  history: { display: 'flex', flexDirection: 'column', gap: 0, marginTop: 6 },
  itemRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    padding: '3px 4px',
    borderBottom: `1px solid ${REXY_CONSOLE.rowDivider}`,
  },
  rowHour: {
    fontFamily: MONO,
    fontSize: 10,
    color: REXY_CONSOLE.mutedText,
    whiteSpace: 'nowrap',
  },
  rowGlyph: { fontFamily: MONO, fontSize: 11, whiteSpace: 'nowrap' },
  rowStatus: {
    fontFamily: MONO,
    fontSize: 9,
    color: REXY_CONSOLE.dimText,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
    flex: 1,
  },
  rowEvents: {
    fontFamily: MONO,
    fontSize: 9,
    color: REXY_CONSOLE.infText,
    whiteSpace: 'nowrap',
  },
  rowFailures: {
    fontFamily: MONO,
    fontSize: 9,
    color: REXY_CONSOLE.critical,
    whiteSpace: 'nowrap',
  },
  emptyHistory: {
    fontFamily: MONO,
    fontSize: 9,
    color: REXY_CONSOLE.dimText,
    padding: '6px 4px 3px 4px',
  },
  critical: {
    fontFamily: MONO,
    fontSize: 10,
    color: REXY_CONSOLE.critical,
    padding: '6px 4px 3px 4px',
  },
  footer: {
    fontFamily: MONO,
    fontSize: 9,
    letterSpacing: 1,
    color: REXY_CONSOLE.dimText,
    marginTop: 8,
  },
  degraded: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.dimText, padding: '4px 0' },
};
